import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { fromJS, List, Map } from 'immutable';

import Loading from '../../shared/Loading';
import TermItem from '../../shared/TermItem';
import checkmark from '../../../images/green_checkmark.svg';
import useRouteStatus from '../../../hooks/useRouteStatus';
import useSessionEntity from '../../../hooks/useSessionEntity';
import { daysAsMonths, formatCurrency, formatPercent } from '../../../lib/formatters';
import { formStyles } from '../../../constants/SiteVariables';
import { getApprovedTerms, updateCompany } from '../../../actions/companies';
import { getRoute, loading } from '../../../actions/sessions';

export default function RechargeSettings({ location }) {
  const dispatch = useDispatch();
  const company = useSessionEntity('company')
  const merchant = useSessionEntity('merchant')
  const isLoading = useSelector(state => state.sessions.get('loading'));
  const [termData, setTermData ] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [valid, setValid] = useState(true);
  const [selectedTerm, setSelectedTerm] = useState({ term: null, term_period: null, payment: null });

  useRouteStatus(location);

  useEffect(() => {
    if (!company || !merchant) return;

    dispatch(loading(true));
    dispatch(getApprovedTerms(company.id, merchant.id))
      .then((res) => setTermData(fromJS(res.data.attributes.terms)))
      .finally(() => dispatch(loading(false)));
  }, []);

  useEffect(() => {
    if (!List.isList(termData)) return;

    const existingSetting = company.recharge_settings.find((s) => {
      return s.get('merchant_id').toString() === merchant.id.toString();
    });

    // Set initial selected term. It can be any term row if some are
    // unavailable or a previsouly selected one.
    const firstAvailable = termData.find(t => {
      return (!existingSetting && t.get('available')) ||
        (t.get('term').toString() == existingSetting.get('default_term').toString() && t.get('available'))
    });

    if (Map.isMap(firstAvailable)) {
      setSelectedTerm({
        term: firstAvailable.get('term'),
        payment: firstAvailable.get('monthly_payment'),
        term_period: firstAvailable.get('term_period'),
        available: firstAvailable.get('available')
      });
    }
  }, [termData]);

  useEffect(() => {
    if (agreed) setValid(true);
  }, [agreed]);

  const availableTerms = !List.isList(termData) ? [] :
    termData.filter(i => i.get('available') && i.get('term_period') === 'monthly')
    .sortBy(t => t.get('term'))
    .map((d, idx) => (
      <TermItem
        key={idx}
        term={d}
        setParentTerm={setSelectedTerm}
        parentSelectedTerm={selectedTerm}
      >
        <div>
          <div className="has-text-centered is-size-5 ">
            <strong>{d.get('term') === 1 ? 'Net 30 / 0% interest' : `${d.get('term')} monthly payments`}</strong>
          </div>
        </div>

        <div>
          <div className="is-one-third has-text-centered has-text-grey is-size-7">
            {d.get('term') === 1 ?
                `One payment, due ${daysAsMonths(merchant.active_promo.get('max_net_duration') + 1)} after the order is processed`
                :
                `${formatPercent(d.get('rate'))} per month after ${daysAsMonths(merchant.active_promo.get('max_net_duration'))}`
            }
          </div>
        </div>
      </TermItem>
    ));

  const save = (values) => {
    if (agreed) {
      dispatch(loading(true));
      return dispatch(updateCompany(company.setRechargeSettings(merchant.id, selectedTerm.term).toJS()))
        .then(() => dispatch(getRoute()))
        .finally(() => dispatch(loading(false)));
    } else {
      setValid(false);
    }
  };

  if (!merchant || !company) {
    return (<Loading active={true} />);
  }

  return (
    <div id="content" className="has-text-centered">
      <div className="mt-3">
        <img
          alt="Green Checkmark"
          src={checkmark}
        />
      </div>

      <div className="mb-3">
        <p className="mb-4 is-size-1 has-text-weight-bold text-green-highlight">
          Good to go
        </p>
      </div>

      <div className="is-size-3">
        {formatCurrency(company.credit_line.get('amount'))} total available credit
      </div>

      <div className="is-size-4 has-text-weight-bold my-5">
        Please select a default term length<br />
        for future {merchant.name} replenishment purchases
      </div>

      <div className="">
        All terms are 0% for {daysAsMonths(merchant.active_promo.get('max_net_duration'))}. No early repayment fees.
      </div>

      {availableTerms}

      <div className="columns is-mobile mb-0">
        <div className="column has-text-left">
          <div className="field">
            <input type="checkbox" className="is-checkradio" id="agree" name="agree" onChange={() => setAgreed(!agreed)} value={agreed} checked={agreed} />
            <label htmlFor="agree">
              I hereby agree to allow {merchant.name} representatives to transact on my
              behalf for any part of my available Credit Key balance, including
              orders up to my entire {formatCurrency(company.credit_line.get('amount'))} credit limit.
            </label>
            {!valid && <div className={formStyles.validation}>
              * Required
            </div>}
          </div>
        </div>
      </div>

      <div className="columns">
        <div className="column">
          <button
            onClick={save}
            type="submit"
            role="submit"
            disabled={isLoading}
            className={classNames('button is-primary is-large is-fullwidth has-text-weight-bold', {
              'is-loading': isLoading
            })}>
            Confirm and Complete
          </button>
        </div>
      </div>
    </div>
  );
}
